'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useState } from 'react'

const navItems = {
  '/': {
    name: 'home',
  },
  '/projects': {
    name: 'projects',
  },
  '/tools': {
    name: 'tools',
  },
}

export function Navbar() {
  const [showToolRequestModal, setShowToolRequestModal] = useState(false);
  
  return (
    <>
      <aside className="-ml-[8px] mb-16 tracking-tight">
        <div className="lg:sticky lg:top-20">
          <nav
            className="flex flex-row items-start relative px-0 pb-0 fade md:overflow-auto scroll-pr-6 md:relative"
            id="nav"
          >
            <div className="flex flex-row items-center justify-between w-full">
              <div className="flex flex-row items-center space-x-4">
                <Link href="/" className="flex-shrink-0">
                  <Image
                    src="/images/grandline-logo-light.png"
                    alt="GrandLine Labs"
                    width={32}
                    height={32}
                    className="dark:invert"
                  />
                </Link>
                {Object.entries(navItems).map(([path, { name }]) => {
                  return (
                    <Link
                      key={path}
                      href={path}
                      className="transition-all hover:text-neutral-800 dark:hover:text-neutral-200 flex align-middle relative py-1 px-2 m-1"
                    >
                      {name}
                    </Link>
                  )
                })}
              </div>
              <button
                onClick={() => setShowToolRequestModal(true)}
                className="transition-all bg-amber-500 hover:bg-amber-600 text-white font-bold flex align-middle relative py-1 px-3 m-1 rounded-md text-sm cursor-pointer"
              >
                Request Tool
              </button>
            </div>
          </nav>
        </div>
      </aside>
      
      {showToolRequestModal && (
        <ToolRequestModal onClose={() => setShowToolRequestModal(false)} />
      )}
    </>
  )
}

function ToolRequestModal({ onClose }: { onClose: () => void }) {
  const [request, setRequest] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!request.trim()) return;
    
    try {
      setIsSubmitting(true);
      
      const response = await fetch('/api/tool-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ request }),
      });
      
      if (response.ok) {
        setMessage('Thank you for your request!');
        setRequest('');
        setTimeout(() => {
          onClose();
          setMessage('');
        }, 2000);
      } else {
        setMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setMessage('An error occurred. Please try again.');
      console.error('Error submitting tool request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 cursor-pointer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <h2 className="text-xl font-bold mb-4 dark:text-white">Request a Tool</h2>
        
        {message ? (
          <div className="text-center py-8 text-green-600 dark:text-green-400">{message}</div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="request" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Describe the tool you'd like us to build
              </label>
              <textarea
                id="request"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
                value={request}
                onChange={(e) => setRequest(e.target.value)}
                placeholder="I would like a tool that..."
                required
              />
            </div>
            
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="mr-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-100 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                disabled={isSubmitting || !request.trim()}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
